import React from 'react';
import { BriefcaseBusinessIcon, UserRoundCheckIcon, UsersRoundIcon, CalendarClockIcon, Building2Icon, SettingsIcon, PlusIcon, XIcon, RefreshCwIcon } from 'lucide-react';
import { org, currentUser } from '../lib/org';
import { kpis } from '../lib/data';
type SidebarProps = {
  mobile?: boolean;
  onClose?: () => void;
};
type NavItem = {
  label: string;
  count?: string;
  tone?: 'brand' | 'attention' | 'consent' | 'ink' | 'neutral';
  active?: boolean;
  icon: typeof BriefcaseBusinessIcon;
};
const workItems: NavItem[] = [{
  label: 'Recrutements actifs',
  count: '4',
  tone: 'brand',
  active: true,
  icon: BriefcaseBusinessIcon
}, {
  label: "Suivi d'intégration",
  count: '2',
  tone: 'attention',
  icon: UserRoundCheckIcon
}, {
  label: 'Registre de talents',
  count: String(kpis.consentedRegistry),
  tone: 'consent',
  icon: UsersRoundIcon
}, {
  label: 'Entrevues à planifier',
  count: '3',
  tone: 'neutral',
  icon: CalendarClockIcon
},
// La boucle. Pas de couleur dédiée (guide V2) : surface encre,
// réservée aux moments forts.
{
  label: 'Apprentissages',
  count: '1',
  tone: 'ink',
  icon: RefreshCwIcon
}];
const adminItems: NavItem[] = [...(org.agencyMode ? [{
  label: 'Organisations',
  icon: Building2Icon
}] : []), {
  label: 'Configuration',
  icon: SettingsIcon
}];
function badgeClasses(tone: NavItem['tone']) {
  if (tone === 'brand') return 'bg-brand-soft text-brand';
  if (tone === 'attention') return 'bg-attention-soft text-attention-text';
  if (tone === 'consent') return 'bg-consent-soft text-consent-text';
  if (tone === 'ink') return 'bg-ink text-white';
  return 'bg-canvas text-muted';
}
function NavigationItem({
  item
}: {
  item: NavItem;
}) {
  const Icon = item.icon;
  return <button type="button" title={item.label} className={`group relative flex w-full items-center gap-3 rounded-card px-3 py-2.5 text-left text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 lg:rounded-none lg:px-4 ${item.active ? 'bg-brand-soft font-500 text-brand lg:border-l-2 lg:border-brand lg:pl-[14px]' : 'text-muted hover:bg-canvas hover:text-ink'}`} data-id="element-210" data-mp="20nif6vub8bk7">
      <Icon className={`h-[17px] w-[17px] flex-none ${item.active ? 'text-brand' : 'text-muted group-hover:text-ink'}`} strokeWidth={1.75} data-id="element-211" data-mp="2h7phocbpuxd2" />
      <span className="hidden min-w-0 flex-1 truncate lg:block" data-id="element-212" data-mp="2882r6lkizbys">{item.label}</span>
      {item.count && <span className={`hidden rounded-full px-2 py-0.5 text-[11px] font-600 lg:block ${badgeClasses(item.tone)}`} data-id="element-213" data-mp="2399ayldut6ux">
          {item.count}
        </span>}
    </button>;
}
export function Sidebar({
  mobile = false,
  onClose
}: SidebarProps) {
  const shellClasses = mobile ? 'fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col border-r border-line bg-white shadow-[8px_0_30px_rgba(10,10,10,0.10)]' : 'sticky top-0 hidden h-screen flex-none border-r border-line bg-white md:flex md:w-20 lg:w-[260px]';
  return <aside className={shellClasses} aria-label="Navigation principale" data-id="element-214" data-mp="1adib7ut2gd8q">
      <div className="flex h-full w-full flex-col px-3 py-5 lg:px-4" data-id="element-215" data-mp="1fd4m3xlf9hed">
        <div className="flex items-start justify-between px-1 lg:px-2" data-id="element-216" data-mp="1fbbcqajfcj2o">
          <div className="min-w-0" data-id="element-217" data-mp="1k4we8ck87v6m">
            {/* Mot-symbole : le X est toujours rouge, jamais recoloré. */}
            <div className="flex items-center" data-id="element-218" data-mp="1o82i5djv9a9x">
              <span className="hidden text-[15px] font-600 tracking-[0.16em] text-ink lg:block" data-id="element-219" data-mp="rgh1xxshbqyz">
                TALEN<span className="text-brand" data-id="element-220" data-mp="we0gkyt3a5rg">X</span>IA
              </span>
              <span className="text-[15px] font-600 text-brand lg:hidden" data-id="element-221" data-mp="10gnbj8l246sv">X</span>
            </div>
          </div>
          {mobile && <button type="button" aria-label="Fermer le menu" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-222" data-mp="15g9ori0vxn1o">
              <XIcon className="h-[18px] w-[18px]" data-id="element-223" data-mp="2aen1x5ctimxi" />
            </button>}
        </div>

        {/* Seul bouton rouge de la vue. */}
        <button type="button" title="Nouveau recrutement" className="mt-8 flex w-full items-center justify-center gap-2 rounded-card bg-brand px-3 py-2.5 text-[13px] font-600 text-white transition-colors hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 lg:justify-start" data-id="element-224" data-mp="2oext69i1i3j7">
          <PlusIcon className="h-4 w-4 flex-none" strokeWidth={2.25} data-id="element-225" data-mp="3813xa51zqqgm" />
          <span className="hidden lg:inline" data-id="element-226" data-mp="333atfio5u8ji">Nouveau recrutement</span>
        </button>

        <nav className="mt-8 space-y-1" data-id="element-227" data-mp="3h2tp3bzsbrex">
          <p className="hidden px-2 pb-2 text-[10px] font-600 tracking-[0.09em] text-muted lg:block" data-id="element-228" data-mp="3h3vaoaeffwaw">MON TRAVAIL</p>
          {workItems.map(item => <NavigationItem key={item.label} item={item} data-id="element-229" data-mp="3c1nwrfa1e65x" />)}
        </nav>

        <div className="my-6 border-t border-line" data-id="element-230" data-mp="3vq25cr7yamxr" />

        <nav className="space-y-1" data-id="element-231" data-mp="4izczwgdmaly">
          <p className="hidden px-2 pb-2 text-[10px] font-600 tracking-[0.09em] text-muted lg:block" data-id="element-232" data-mp="4hwplyi9iqas">ADMINISTRATION</p>
          {adminItems.map(item => <NavigationItem key={item.label} item={item} data-id="element-233" data-mp="3qqfrqgo7n4of" />)}
        </nav>

        <div className="mt-auto border-t border-line pt-4" data-id="element-234" data-mp="3qomkp09oqifw">
          <div className="flex items-center gap-3 px-1 lg:px-2" data-id="element-235" data-mp="3cyrega286a5v">
            <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-ink text-[12px] font-600 text-white" data-id="element-236" data-mp="3hprbrgf9au8w">
              {currentUser.initials}
            </div>
            <div className="hidden min-w-0 flex-1 lg:block" data-id="element-237" data-mp="32z0kc9hktx6b">
              <p className="truncate text-[13px] font-500 text-ink" data-id="element-238" data-mp="380q0g3jnp119">{currentUser.name}</p>
              <p className="truncate text-[11px] text-muted" data-id="element-239" data-mp="2u185f6frorjg">{currentUser.role}</p>
            </div>
            <button type="button" aria-label={`Paramètres de ${currentUser.name}`} className="hidden h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 lg:flex" data-id="element-240" data-mp="2yy8bhhcm9uak">
              <SettingsIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-241" data-mp="qrn0tc7bfgx9" />
            </button>
          </div>
        </div>
      </div>
    </aside>;
}